import React from 'react';
import { RenderNode } from '@contentful/rich-text-react-renderer';
import { BLOCKS, MARKS } from '@contentful/rich-text-types';

import { BlockContainer, Layout, Column } from '../../Layout';
import RichText from '../../RichText';
import { EmbeddedEntryNode } from '../../RichText/Nodes';
import { Text } from '../../Text';
import { Flexbox } from '../../Flexbox';

import {
  Table,
  Row,
  HeaderCell,
  Cell,
  Paragraph,
  FootnoteLink
} from './styles';

import { TableProps } from '../shared';

interface BasicTableProps extends TableProps {
  basic?: boolean;
}

export function BasicTable({
  title,
  subtitle,
  tableId,
  tableBody,
  tableFootnotes,
  basic = true
}: BasicTableProps) {
  const RICH_TEXT_OVERRIDES: RenderNode = {
    [BLOCKS.TABLE]: (_node, children) => (
      <Table className="basic-table" basic={basic}>
        <tbody>{children}</tbody>
      </Table>
    ),
    [BLOCKS.TABLE_ROW]: (_node, children) => (
      <Row className="basic-row">{children}</Row>
    ),
    [BLOCKS.TABLE_HEADER_CELL]: (_node, children) => (
      <HeaderCell
        className="basic-header"
        basic={basic}
        data-testid="basic-header"
      >
        {children}
      </HeaderCell>
    ),
    [BLOCKS.TABLE_CELL]: (_node, children) => (
      <Cell className="basic-cell">{children}</Cell>
    ),
    [BLOCKS.PARAGRAPH]: (_node, children) => <Paragraph>{children}</Paragraph>,
    [BLOCKS.EMBEDDED_ENTRY]: (node, children) => (
      <Flexbox margin={['contentMargin', 0]}>
        <EmbeddedEntryNode node={node} colour="darkButton">
          {children}
        </EmbeddedEntryNode>
      </Flexbox>
    )
  };

  return (
    <BlockContainer
      id={tableId!}
      data-blockid="basic"
      data-testid="basic-table"
    >
      <Layout paddingTop={{ 0: 'l', tablet: '3xl' }}>
        <Column size={{ 0: 12, tablet: 8 }} flexDirection="column">
          {title && (
            <Text
              fontSize={{ 0: '2xl', desktop: '4xl' }}
              fontWeight="600"
              margin={['contentMargin', 0]}
            >
              {title}
            </Text>
          )}

          {subtitle && (
            <Text
              fontSize={{ 0: '2xl', desktop: '4xl' }}
              fontWeight="600"
              margin={[0, 0, 'contentMargin']}
            >
              {subtitle}
            </Text>
          )}

          <RichText
            data={tableBody}
            overrideOptions={RICH_TEXT_OVERRIDES}
            overrideMarkOptions={{
              [MARKS.CODE]: (children) => (
                <FootnoteLink
                  id={`${tableId}-footnote-ref-${children}`.trim()}
                  href={`#${tableId}-footnote-${children}`.trim()}
                  aria-describedby={`${tableId}-footnotes`}
                  role="doc-noteref"
                >
                  <sup>{children}</sup>
                </FootnoteLink>
              )
            }}
          />

          {tableFootnotes && (
            <div id={`${tableId}-footnotes`}>
              <RichText
                data={tableFootnotes}
                overrideOptions={RICH_TEXT_OVERRIDES}
                overrideMarkOptions={{
                  [MARKS.CODE]: (children) => (
                    <FootnoteLink
                      id={`${tableId}-footnote-${children}`.trim()}
                      href={`#${tableId}-footnote-ref-${children}`.trim()}
                    >
                      <sup>{children}</sup>
                    </FootnoteLink>
                  )
                }}
              />
            </div>
          )}
        </Column>
      </Layout>
    </BlockContainer>
  );
}
